<script lang="ts">
  // Overview > Pulse — the day roster, grouped by site. One row per active
  // employee who punched at all on the selected day: when they arrived,
  // when they left, and how long they were credited for.
  //
  // Grouping is the point. A flat arrival-sorted list mixed Hoskote Factory
  // with Office and hid the site on phones (mobile cards dropped that
  // column), so two people still in at different sites read as one pile.
  //
  // This replaced the separate "Punched in now" and "Punched out today"
  // disclosures. A still-in row answers both questions at once, and unlike
  // either list it doesn't lose the person who punched out at lunch and came
  // back — that person was in neither list cleanly before.
  //
  // Two things this view must never state:
  //   - an auto-closed session's punch_out as a departure time (it's
  //     punch_in, set by attendance_sweep.go as bookkeeping), and
  //   - "0.0h" for that session, which reads as "came and left instantly".
  // Both render as "not recorded" instead. See internal/analytics/dayfacts.go.
  import { createQuery } from '@tanstack/svelte-query';
  import { ChevronDown, ChevronRight, MapPin } from 'lucide-svelte';
  import type { HRDayRosterEntry, HRDayRosterSession } from '$lib/types';
  import { hrOverviewApi } from '$lib/api/hr';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import { leafletMap, type LeafletMapParams } from '$lib/utils/leafletMap';

  const {
    rows,
    isToday,
    loading = false
  }: { rows: HRDayRosterEntry[]; isToday: boolean; loading?: boolean } = $props();

  // A still-in row's hours have to keep moving without a refetch, so the
  // open stretch is computed client-side from open_since against a ticking
  // clock rather than baked into the payload (the server never credits an
  // open session — see DayFact.Seconds).
  let now = $state(Date.now());
  $effect(() => {
    if (!isToday || !rows.some((r) => r.still_in)) return;
    const id = setInterval(() => (now = Date.now()), 60_000);
    return () => clearInterval(id);
  });

  let expanded = $state<Record<number, boolean>>({});
  let locationPerson = $state<HRDayRosterEntry | null>(null);
  let locationSession = $state<HRDayRosterSession | null>(null);
  const locationQuery = createQuery(() => ({
    queryKey: ['hr-punch-location', locationSession?.work_log_id],
    queryFn: () => hrOverviewApi.workLogLocation(locationSession!.work_log_id),
    enabled: locationPerson !== null && locationSession !== null,
    // A missing immutable session/location cannot become available by retrying.
    retry: false
  }));

  function toggle(userId: number) {
    expanded = { ...expanded, [userId]: !expanded[userId] };
  }

  function fmtTime(dt: string): string {
    return new Date(dt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  function liveHours(row: HRDayRosterEntry): number {
    if (!row.still_in || !row.open_since) return row.hours;
    const open = (now - new Date(row.open_since).getTime()) / 3_600_000;
    return Math.round((row.hours + Math.max(0, open)) * 10) / 10;
  }

  type Tone = 'good' | 'warn' | 'bad' | 'idle';

  function tone(row: HRDayRosterEntry): Tone {
    if (row.still_in) return 'good';
    // An open session on a day that's over, or an uncorrected auto-close,
    // is a punch-out that never happened — that's the thing HR acts on.
    if (row.open_since || row.forgot_punch_out) return 'warn';
    return 'idle';
  }

  function toneLabel(row: HRDayRosterEntry): string {
    if (row.still_in) return 'Still in';
    if (row.open_since) return 'Never punched out';
    if (row.forgot_punch_out) return 'Auto-closed';
    return 'Punched out';
  }

  function sessionHours(s: HRDayRosterSession): string {
    if (!s.punch_out) return '—';
    if (s.forgot_punch_out && !s.forgot_fixed) return 'not recorded';
    return `${s.hours}h`;
  }

  function sessionOut(s: HRDayRosterSession): string {
    if (!s.punch_out) return 'still open';
    if (s.forgot_punch_out && !s.forgot_fixed) return 'auto-closed';
    return fmtTime(s.punch_out);
  }

  function sessionSite(s: HRDayRosterSession): string {
    if (s.work_zone_name) return s.work_zone_name;
    if (s.remote) return s.location_name || 'Remote';
    return '';
  }

  function lateLabel(s: HRDayRosterSession): string {
    if (!s.late) return '';
    return `Late · ${s.late_by_minutes ?? 0}m`;
  }

  // Current site for a still-in row (the open session), else the payload's
  // last/current site. Mirrors handlers.rosterSiteName so a stale payload
  // still groups the person where they actually are.
  function siteOf(row: HRDayRosterEntry): string {
    if (row.still_in) {
      const open = [...row.sessions].reverse().find((s) => !s.punch_out);
      const fromOpen = open ? sessionSite(open) : '';
      if (fromOpen) return fromOpen;
    }
    if (row.work_zone_name) return row.work_zone_name;
    if (row.remote) return row.sessions.find((s) => s.location_name)?.location_name || 'Unnamed place';
    return 'Sales';
  }

  type SiteGroup = { key: string; label: string; stillIn: number; rows: HRDayRosterEntry[] };

  function siteRank(key: string): number {
    if (key === 'Sales' || key === 'Home' || key === 'Unnamed place') return 2;
    return 0;
  }

  const groups = $derived.by((): SiteGroup[] => {
    const buckets = new Map<string, HRDayRosterEntry[]>();
    for (const row of rows) {
      const key = siteOf(row);
      const list = buckets.get(key);
      if (list) list.push(row);
      else buckets.set(key, [row]);
    }
    return [...buckets.keys()]
      .sort((a, b) => {
        const ra = siteRank(a);
        const rb = siteRank(b);
        if (ra !== rb) return ra - rb;
        return a.localeCompare(b);
      })
      .map((key) => {
        const siteRows = buckets.get(key) ?? [];
        return {
          key,
          label: key,
          stillIn: siteRows.filter((r) => r.still_in).length,
          rows: siteRows
        };
      });
  });

  function openPerson(row: HRDayRosterEntry) {
    route.push({ tab: 'directory', sub: 'person', userId: row.user_id });
  }

  function currentSession(row: HRDayRosterEntry): HRDayRosterSession | null {
    return [...row.sessions].reverse().find((s) => !s.punch_out) ?? null;
  }

  function latestLocatedSession(row: HRDayRosterEntry): HRDayRosterSession | undefined {
    const open = [...row.sessions].reverse().find((s) => !s.punch_out && s.has_punch_in_location);
    if (open) return open;
    return [...row.sessions].reverse().find((s) => s.has_punch_in_location);
  }

  function openLocation(event: MouseEvent, row: HRDayRosterEntry, s: HRDayRosterSession) {
    event.stopPropagation();
    locationPerson = row;
    locationSession = s;
  }

  function punchMapParams(location: NonNullable<typeof locationQuery.data>): LeafletMapParams {
    if (location.punch_in_lat == null || location.punch_in_lng == null) return { points: [] };
    return {
      points: [
        {
          lat: location.punch_in_lat,
          lng: location.punch_in_lng,
          color: '#1d4ed8',
          tooltip: 'Punch-in location',
          accuracyM: location.punch_in_accuracy_m
        }
      ]
    };
  }
</script>

{#snippet lampCell(row: HRDayRosterEntry)}
  <StatusLamp tone={tone(row)} pulse={row.still_in} label={toneLabel(row)} />
{/snippet}

{#snippet nameCell(row: HRDayRosterEntry)}
  <div class="min-w-0">
    {#if latestLocatedSession(row) && !session.isFieldScoped}
      <button
        type="button"
        class="inline-flex max-w-full items-center gap-1 truncate font-medium text-navy hover:text-toyama-teal hover:underline"
        onclick={(event) => openLocation(event, row, latestLocatedSession(row)!)}
        onkeydown={(event) => event.stopPropagation()}
        aria-label={`Show ${row.name}'s punch-in location`}
      >
        <MapPin class="h-3.5 w-3.5 shrink-0" />
        <span class="truncate">{row.name}</span>
      </button>
    {:else}
      <div class="truncate font-medium text-navy">{row.name}</div>
    {/if}
    <div class="truncate text-[11.5px] text-dark-gray">{siteOf(row)}</div>
    {#if row.session_count > 1}
      <!-- stopPropagation: the row itself navigates to the person page, and
           opening the session breakdown is not that. -->
      <button
        type="button"
        class="mt-0.5 inline-flex items-center gap-1 text-[11.5px] text-dark-gray hover:text-toyama-teal"
        onclick={(e) => {
          e.stopPropagation();
          toggle(row.user_id);
        }}
      >
        {#if expanded[row.user_id]}
          <ChevronDown class="h-3 w-3" />
        {:else}
          <ChevronRight class="h-3 w-3" />
        {/if}
        {row.session_count} sessions
      </button>
      {#if expanded[row.user_id]}
        <ul class="mt-1.5 space-y-1 border-l border-toyama-cloudy-gray pl-2.5 text-[11.5px] text-dark-gray">
          {#each row.sessions as s (s.work_log_id)}
            <li class="tabular-nums">
              {#if s.has_punch_in_location && !session.isFieldScoped}<button class="text-toyama-teal underline" type="button" onclick={(e) => openLocation(e, row, s)}>pin</button> {/if}{fmtTime(s.punch_in)} → {sessionOut(s)} · {sessionHours(s)}{#if sessionSite(s)}
                · {sessionSite(s)}{/if}{#if lateLabel(s)} · <span class="font-semibold text-warning">{lateLabel(s)}</span>{/if}
            </li>
          {/each}
        </ul>
      {/if}
    {/if}
  </div>
{/snippet}

{#snippet inCell(row: HRDayRosterEntry)}
  <span class="tabular-nums text-navy">{fmtTime(row.first_in)}</span>
  {#if row.sessions.some((s) => s.late)}<span class="ml-1 text-[11px] font-semibold text-warning">Late</span>{/if}
{/snippet}

{#snippet outCell(row: HRDayRosterEntry)}
  {#if row.still_in}
    <span class="font-medium text-toyama-teal">still in</span>
  {:else if row.last_out}
    <span class="tabular-nums text-navy">{fmtTime(row.last_out)}</span>
  {:else if row.open_since}
    <span class="text-warning">not punched out</span>
  {:else if row.forgot_punch_out}
    <span class="text-dark-gray">auto-closed</span>
  {:else}
    <span class="text-dark-gray">—</span>
  {/if}
{/snippet}

{#snippet hoursCell(row: HRDayRosterEntry)}
  {#if row.forgot_punch_out && row.hours === 0}
    <!-- An auto-closed day credits zero hours by construction. Printing
         "0.0h" would state that they came and left instantly. -->
    <span class="text-[12px] text-dark-gray">not recorded</span>
  {:else}
    <span class="font-semibold tabular-nums text-navy">{liveHours(row)}h</span>
  {/if}
{/snippet}

{#snippet rosterTable(tableRows: HRDayRosterEntry[], showEmpty: boolean)}
  <DataTable
    columns={[
      { key: 'lamp', label: '', width: '28px', cell: lampCell },
      { key: 'name', label: 'Name', cell: nameCell },
      { key: 'in', label: 'In', cell: inCell },
      { key: 'out', label: 'Out', cell: outCell },
      { key: 'hours', label: 'Hours', align: 'right', cell: hoursCell }
    ]}
    rows={tableRows}
    {loading}
    rowKey={(r) => r.user_id}
    onRowClick={session.isFieldScoped ? undefined : openPerson}
    mobileCards
    mobileColumns={['name', 'in', 'out', 'hours']}
    dense
    emptyTitle={showEmpty ? 'Nobody punched in' : undefined}
    emptyBody={showEmpty
      ? isToday
        ? 'No one has punched in yet today.'
        : 'There are no punches on this day.'
      : undefined}
  />
{/snippet}

{#if loading || rows.length === 0}
  {@render rosterTable(rows, true)}
{:else}
  <div class="space-y-4">
    {#each groups as g (g.key)}
      <div>
        <div class="mb-2 flex items-baseline justify-between gap-3">
          <p class="text-[13.5px] font-semibold text-navy">{g.label}</p>
          <p class="tym-section-sub !mt-0">
            {#if isToday && g.stillIn > 0}
              {g.stillIn} still in · {g.rows.length}
            {:else}
              {g.rows.length}
            {/if}
          </p>
        </div>
        {@render rosterTable(g.rows, false)}
      </div>
    {/each}
  </div>
{/if}

{#if locationPerson}
  <Modal title={`${locationPerson.name} — punch-in location`} onClose={() => { locationPerson = null; locationSession = null; }} wide>
    {#if locationQuery.isLoading}
      <div class="tym-loading-state">Loading punch-in location...</div>
    {:else if locationQuery.isError}
      <div class="tym-error-state">Could not load this captured punch-in location.</div>
    {:else if locationQuery.data?.punch_in_lat != null && locationQuery.data.punch_in_lng != null}
      <div
        class="h-[320px] w-full overflow-hidden rounded-md border border-toyama-cloudy-gray"
        use:leafletMap={punchMapParams(locationQuery.data)}
      ></div>
      <div class="mt-3 flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-dark-gray">
        <span>
          Captured at {fmtTime(locationQuery.data.punch_in)}{#if locationQuery.data.punch_in_accuracy_m != null}
            · accurate to about {Math.round(locationQuery.data.punch_in_accuracy_m)} m{/if}
        </span>
        <a
          class="font-medium text-toyama-teal underline"
          href={`https://www.google.com/maps?q=${locationQuery.data.punch_in_lat},${locationQuery.data.punch_in_lng}`}
          target="_blank"
          rel="noopener noreferrer"
        >Open in Google Maps</a>
      </div>
      <p class="mt-2 text-[11.5px] text-dark-gray">This is the location captured when they punched in, not live tracking.</p>
    {:else}
      <div class="tym-empty-state">
        No GPS location was captured for this punch-in. This can happen for punch modes that do not collect location.
      </div>
    {/if}
  </Modal>
{/if}
